import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import { InterfaceChainUtils, type InterfaceToken, type Rewards } from '@morpho-org/dapp-core';
import { NetworkAvatar as Network } from '@morpho-org/ui-v6/client';
import { formatters, getDigitsFromPriceAndAmount, UNKNOWN_SYMBOL } from '@morpho-org/ui-v6/utils';
import { Badge, Button, Separator } from '@repo/ui';
import { useMemo } from 'react';
import type { Warning } from '@/components/common/WarningIcon';
import { MobileCardRow } from '@/components/common/MobileCardRow';
import MobileBorrowHeader from './components/MobileBorrowHeader';
import { useMobileLendCard } from './hooks/useMobileLendCard';
const srcAvatarProps = {
  size: 'small'
} as const;
const lltvFormatter = formatters.percent.default('-').digits(2).trailingZero(false).unit('%');
interface MobileLendPositionCardProps {
  href?: string;
  onLinkClick?: () => void;
  marketId: MarketId;
  collateralToken: InterfaceToken | null;
  lent: {
    token: InterfaceToken;
    amount?: bigint | null;
    usdValue?: bigint | null;
  };
  /** Market LLTV as a unit fraction (e.g. `0.945`). */
  lltv: number;
  netApy?: bigint | null;
  apy?: bigint | null;
  rewards: Rewards[];
  warnings: Warning[];
  displayNetwork?: boolean;
  /** False when browsing another address's portfolio — hides the Withdraw action. */
  isSelf?: boolean;
  /** One position per side. `undefined` while the answer has not landed; Lend stays hidden. */
  hasBorrowPosition?: boolean;
}
export default function MobileLendPositionCard(t0) {
  const $ = _c(55);
  const {
    href,
    onLinkClick,
    marketId,
    collateralToken,
    lent,
    lltv,
    netApy,
    apy,
    rewards,
    warnings,
    displayNetwork,
    isSelf: t1,
    hasBorrowPosition
  } = t0;
  const isSelf = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== lltv) {
    t2 = lltvFormatter.of(lltv);
    $[0] = lltv;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const formattedLltv = t2;
  let t3;
  if ($[2] !== hasBorrowPosition || $[3] !== isSelf || $[4] !== lent.token.chain.id || $[5] !== marketId) {
    t3 = {
      marketId,
      chainId: lent.token.chain.id,
      isSelf,
      hasBorrowPosition
    };
    $[2] = hasBorrowPosition;
    $[3] = isSelf;
    $[4] = lent.token.chain.id;
    $[5] = marketId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    onWithdrawClick,
    canWithdraw,
    onLendClick,
    canLend
  } = useMobileLendCard(t3);
  let t4;
  if ($[7] !== lent.amount || $[8] !== lent.token.decimals || $[9] !== lent.token.price || $[10] !== lent.token.symbol) {
    t4 = formatters.short.digits(getDigitsFromPriceAndAmount(lent.token.price, lent.amount, lent.token.decimals)).readable().unit(lent.token.symbol).of(lent.amount, lent.token.decimals);
    $[7] = lent.amount;
    $[8] = lent.token.decimals;
    $[9] = lent.token.price;
    $[10] = lent.token.symbol;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== lent.usdValue) {
    t5 = formatters.short.unit("$").digits(2).of(lent.usdValue, 18);
    $[12] = lent.usdValue;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = {
      formattedLentAmount: t4,
      formattedLentUsd: t5
    };
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const {
    formattedLentAmount,
    formattedLentUsd
  } = t6;
  let t7;
  if ($[17] !== lent.token.chain.id) {
    t7 = InterfaceChainUtils.getMetadata(lent.token.chain.id);
    $[17] = lent.token.chain.id;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const networkName = t7.chain.name;
  const t8 = collateralToken?.logoURI;
  const t9 = collateralToken ? collateralToken.symbol ?? UNKNOWN_SYMBOL : undefined;
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== apy || $[21] !== formattedLltv || $[22] !== href || $[23] !== lent.token || $[24] !== netApy || $[25] !== onLinkClick || $[26] !== rewards || $[27] !== t8 || $[28] !== t9 || $[29] !== warnings) {
    t11 = <MobileBorrowHeader prefetch={true} href={href} onLinkClick={onLinkClick} loanLogoURI={lent.token.logoURI} loanSymbol={lent.token.symbol} collateralLogoURI={t8} collateralSymbol={t9} lltv={formattedLltv} netApy={netApy} apy={apy} rewards={rewards} points={t10} rewardAsset={lent.token} rewardsVariant="supply" warnings={warnings} />;
    $[20] = apy;
    $[21] = formattedLltv;
    $[22] = href;
    $[23] = lent.token;
    $[24] = netApy;
    $[25] = onLinkClick;
    $[26] = rewards;
    $[27] = t8;
    $[28] = t9;
    $[29] = warnings;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Separator className="my-xs" />;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== displayNetwork || $[33] !== lent.token.chain.id || $[34] !== networkName) {
    t13 = displayNetwork && <MobileCardRow label="Network"><div className="gap-2xs flex items-center"><Network chainId={lent.token.chain.id} srcAvatarProps={srcAvatarProps} /><span className="text-3xs capitalize">{networkName}</span></div></MobileCardRow>;
    $[32] = displayNetwork;
    $[33] = lent.token.chain.id;
    $[34] = networkName;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      lineHeight: "normal"
    };
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== formattedLentAmount) {
    t15 = <span className="text-3xs text-foreground" style={t14}>{formattedLentAmount}</span>;
    $[37] = formattedLentAmount;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== formattedLentAmount || $[40] !== formattedLentUsd) {
    t16 = formattedLentAmount && formattedLentUsd && <Badge variant="tertiary" size="sm" className="my-auto">{formattedLentUsd}</Badge>;
    $[39] = formattedLentAmount;
    $[40] = formattedLentUsd;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t15 || $[43] !== t16) {
    t17 = <MobileCardRow label="Lent"><div className="gap-2xs flex items-center">{t15}{t16}</div></MobileCardRow>;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== canLend || $[46] !== canWithdraw || $[47] !== onLendClick || $[48] !== onWithdrawClick) {
    t18 = (canLend || canWithdraw) && <><Separator className="my-xs" /><div className="gap-sm flex">{canLend && <Button className="flex-1" data-testid="mobile-lend" onClick={onLendClick}>Lend</Button>}{canWithdraw && <Button className="flex-1" variant={canLend ? "secondary" : undefined} data-testid="mobile-lend-withdraw" onClick={onWithdrawClick}>Withdraw</Button>}</div></>;
    $[45] = canLend;
    $[46] = canWithdraw;
    $[47] = onLendClick;
    $[48] = onWithdrawClick;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t11 || $[51] !== t13 || $[52] !== t17 || $[53] !== t18) {
    t19 = <article className="text-foreground p-md gap-sm bg-card flex flex-col rounded-[20px] shadow-md dark:shadow-none">{t11}{t12}{t13}{t17}{t18}</article>;
    $[50] = t11;
    $[51] = t13;
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  return t19;
}